import React, { useContext } from 'react';
import styled from 'styled-components';
import { FlexRow } from '../../shared/FlexRow';
import { TextButton } from '../../shared/styled-components/styled_buttons';
import { AppContext } from '../../App';
import { setSavedAuth } from '../utils/storage';
import { appRoutes } from '../../AppRoutes';
import { useNavigate } from 'react-router-dom';
import { useTrack } from '@hackler/react-sdk';

interface Props {
  step?: 'create' | 'invite' | 'welcome';
  createStep?: number;
  children?: React.ReactNode;
}

export const OnboardingContainer: React.FC<Props> = ({ step, createStep, children }) => {
  const navigate = useNavigate();
  const { isMobile, userInfo } = useContext(AppContext);
  const track = useTrack();

  const handleLogout = () => {
    if (step !== 'invite') {
      track({ key: `click_step${step === 'welcome' ? '0' : createStep === 1 ? '1' : '2'}_logout` });
    }

    setSavedAuth(null);
    navigate(appRoutes.login.path());
  };

  const handleAccountInfo = () => {
    if (step !== 'invite') {
      track({ key: `click_step${step === 'welcome' ? '0' : createStep === 1 ? '1' : '2'}_accountInfo` });
    }

    userInfo.open();
  };

  const handleBackButton = () => {
    if (step === 'create') {
      track({ key: `click_step${createStep}_back` });

      navigate(-1);
    } else {
      navigate(appRoutes.welcome.path());
    }
  };

  return isMobile ? (
    <MobileContainer>
      <MobileHeader>
        <button onClick={handleBackButton}>
          <MobileButtonIcon $src={step === 'create' ? 'chevron' : 'home'} />
        </button>
        <FlexRow width={'fit-content'} justify={'flex-end'} align={'center'} gap={'0.2rem'}>
          <TextButton size={'m'} color={'mono'} onClick={handleAccountInfo}>
            계정정보
          </TextButton>
          <TextButton size={'m'} color={'mono'} onClick={handleLogout}>
            로그아웃
          </TextButton>
        </FlexRow>
      </MobileHeader>
      {children}
    </MobileContainer>
  ) : (
    <Container>
      <Header>
        <TitleWrapper>
          <button>
            <img src={'/images/common/logo.svg'} alt={'logo'} style={{ width: '14.8789rem', height: '2.2374rem' }} />
          </button>
          <FlexRow width={'fit-content'} justify={'flex-end'} align={'center'} gap={'1.4rem'}>
            <TextButton size={'m'} color={'mono'} onClick={handleAccountInfo}>
              계정정보
            </TextButton>
            <TextButton size={'m'} color={'mono'} onClick={handleLogout}>
              로그아웃
            </TextButton>
          </FlexRow>
        </TitleWrapper>
      </Header>
      {children}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: 100%;
  background-color: var(--background-2);

  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  width: 100%;
  padding: 2.4rem 3.2rem;
  background-color: var(--background-1);
  border-bottom: 1px solid var(--border-color);
  box-sizing: border-box;
  z-index: 5;
`;

const TitleWrapper = styled.div`
  width: 100%;
  max-width: 144rem;
  margin: 0 auto;
  padding-inline: 2.4rem;
  box-sizing: border-box;

  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const MobileContainer = styled.div`
  width: 100%;
  height: 100%;
  background-color: var(--background-1);
  box-sizing: border-box;
  overflow: hidden;
`;

const MobileHeader = styled.div`
  width: 100%;
  height: fit-content;
  min-height: 5.6rem;
  padding: calc(env(safe-area-inset-top, 4.4rem)) 1.6rem 1.2rem;
  background-color: var(--background-1);
  border-bottom: 1px solid var(--border-color);
  box-sizing: border-box;
  position: relative;
  z-index: 5;

  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const MobileButtonIcon = styled.div<{ $src: string }>`
  width: 2.4rem;
  height: 2.4rem;
  mask-image: url('/images/common/${props => props.$src}.svg');
  mask-size: contain;
  mask-repeat: no-repeat;
  background-color: var(--content-color-secondary);
`;
